/**
 * Sidebar Component
 * Requirements: 3.4, 3.6, 3.7, 3.8
 */
import type { JobPhase, UserPreferences } from '../types';
import { VALID_PHASES, ACTIVE_PHASES } from '../types';
import { PHASE_COLORS } from '../constants';
import {
  Plus,
  Filter,
  Search
} from 'lucide-react';

interface SidebarProps {
  selectedPhase: JobPhase | 'active' | 'all';
  onSelectPhase: (phase: JobPhase | 'active' | 'all') => void;
  phaseCounts: Record<JobPhase, number>;
  onAddJobUrl: () => void;
  onAddJobManual: () => void;
  onAddJobEmail: () => void;
  userPreferences?: UserPreferences;
  searchQuery: string;
  onSearchChange: (query: string) => void;
}

export function Sidebar({
  selectedPhase,
  onSelectPhase,
  phaseCounts,
  onAddJobUrl,
  onAddJobManual,
  onAddJobEmail,
  userPreferences,
  searchQuery,
  onSearchChange,
}: SidebarProps) {
  const totalActive = ACTIVE_PHASES.reduce((sum, phase) => sum + (phaseCounts[phase] || 0), 0);
  const totalAll = VALID_PHASES.reduce((sum, phase) => sum + (phaseCounts[phase] || 0), 0);

  // Generate tooltip text for each phase based on user preferences
  const getPhaseTooltip = (phase: JobPhase): string => {
    const prefs = userPreferences;

    // Default values from types/index.ts comments
    const daysQueued = prefs?.days_queued_before_expired ?? 90;
    const daysGenerating = prefs?.days_generating_before_expired ?? 90;
    const daysReady = prefs?.days_ready_before_expired ?? 90;
    const daysApplied = prefs?.days_applied_before_expired ?? 90;
    const daysNegotiation = prefs?.days_negotiation_before_expired ?? 90;
    const daysAccepted = prefs?.days_accepted_before_expired ?? 365;
    const daysDeclined = prefs?.days_declined_before_expired ?? 365;
    const daysSkipped = prefs?.days_skipped_before_trashed ?? 10;
    const daysErrored = prefs?.days_errored_before_trashed ?? 10;
    const daysExpired = prefs?.days_expired_before_trashed ?? 30;
    const daysTrash = prefs?.days_trash_before_deleted ?? 30;

    const tooltips: Record<JobPhase, string> = {
      'Queued': `Jobs added that have not yet been processed. Jobs will move to 'Expired' after ${daysQueued} days.`,
      'Generating': `AI is generating custom content for this job. Jobs will move to 'Expired' after ${daysGenerating} days.`,
      'Ready': `Resume and Cover Letter are ready for review. Jobs will move to 'Expired' after ${daysReady} days.`,
      'Applied': `Application has been submitted to the company. Jobs will move to 'Expired' after ${daysApplied} days.`,
      'Follow-Up': `Following up on the application. Jobs will move to 'Expired' after ${daysApplied} days.`,
      'Negotiation': `Salary negotiation in progress. Jobs will move to 'Expired' after ${daysNegotiation} days.`,
      'Declined': `Offer was declined. Jobs will move to 'Expired' after ${daysDeclined} days.`,
      'Accepted': `Offer accepted - Congratulations! Jobs will move to 'Expired' after ${daysAccepted} days.`,
      'Skipped': `Job was skipped. Jobs will move to 'Trash' after ${daysSkipped} days.`,
      'Errored': `Job had an error during processing. Jobs will move to 'Trash' after ${daysErrored} days.`,
      'Expired': `Job has expired. Jobs will move to 'Trash' after ${daysExpired} days.`,
      'Trash': `Job is in the trash. Jobs will be deleted after ${daysTrash} days.`,
    };

    return tooltips[phase];
  };

  return (
    <aside className="w-64 bg-white border-r border-gray-200 h-full overflow-y-auto">
      <div className="p-4 space-y-6">
        {/* Add Job Button */}
        <div className="relative">
          <AddJobDropdown
            onAddJobUrl={onAddJobUrl}
            onAddJobManual={onAddJobManual}
            onAddJobEmail={onAddJobEmail}
          />
        </div>

        {/* Search Box */}
        <div className="relative px-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input
            type="text"
            placeholder="Search jobs..."
            aria-label="Search jobs"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-9 pr-3 py-2 w-full border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>

        {/* Phase Filters */}
        <div>
          <div className="flex items-center mb-2">
            <Filter className="w-4 h-4 mr-2 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Phases</span>
          </div>

          <div className="space-y-1">
            {VALID_PHASES.map((phase) => (
              <div key={phase} className="relative group">
                <button
                  onClick={() => onSelectPhase(phase)}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                    selectedPhase === phase
                      ? 'bg-primary-100 text-primary-700'
                      : 'hover:bg-gray-100 text-gray-700'
                  }`}
                >
                  <span className="flex items-center">
                    <span className={`w-2 h-2 rounded-full mr-2 ${PHASE_COLORS[phase].split(' ')[0]}`} />
                    {phase}
                  </span>
                  <span className={`px-2 py-0.5 rounded-full text-xs ${PHASE_COLORS[phase]}`}>
                    {phaseCounts[phase] || 0}
                  </span>
                </button>
                {/* Tooltip with 0.5s delay - appears below button */}
                <div
                  className="absolute left-0 top-full mt-1 z-50 pointer-events-none opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200"
                  style={{ transitionDelay: '500ms' }}
                >
                  <div className="bg-gray-900 text-white text-xs rounded-lg py-2 px-3 max-w-[200px] shadow-lg whitespace-normal">
                    {getPhaseTooltip(phase)}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <hr className="my-3" />

          {/* Aggregations */}
          <div className="space-y-1">
            <button
              onClick={() => onSelectPhase('active')}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                selectedPhase === 'active'
                  ? 'bg-primary-100 text-primary-700'
                  : 'hover:bg-gray-100 text-gray-700'
              }`}
            >
              <span>All Active</span>
              <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-800">
                {totalActive}
              </span>
            </button>
            <button
              onClick={() => onSelectPhase('all')}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                selectedPhase === 'all'
                  ? 'bg-primary-100 text-primary-700'
                  : 'hover:bg-gray-100 text-gray-700'
              }`}
            >
              <span>All Jobs</span>
              <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-800">
                {totalAll}
              </span>
            </button>
          </div>
        </div>
      </div>
      {/* Footer Links */}
      <div className="mt-auto p-4 border-t border-gray-200">
        <div className="flex justify-center gap-4 text-xs text-gray-500">
          <a href="/docs/toc.html" target="_blank" rel="noopener noreferrer" className="hover:text-primary-600 transition-colors">Terms</a>
          <a href="/docs/pp.html" target="_blank" rel="noopener noreferrer" className="hover:text-primary-600 transition-colors">Privacy</a>
        </div>
      </div>
    </aside>
  );
}

// Add Job Dropdown component for sidebar
import { useState } from 'react';
import { Link, FileText, ChevronDown, Mail } from 'lucide-react';

function AddJobDropdown({
  onAddJobUrl,
  onAddJobManual,
  onAddJobEmail,
}: {
  onAddJobUrl: () => void;
  onAddJobManual: () => void;
  onAddJobEmail: () => void;
}) {
  const [showMenu, setShowMenu] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setShowMenu(!showMenu)}
        data-tutorial="add-job"
        className="w-full flex items-center justify-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
      >
        <Plus className="w-4 h-4 mr-2" />
        <span>Add Job</span>
        <ChevronDown className="w-4 h-4 ml-2" />
      </button>
      
      {showMenu && (
        <div role="menu" className="absolute left-0 right-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
          <button
            role="menuitem"
            onClick={() => { onAddJobUrl(); setShowMenu(false); }}
            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
          >
            <Link className="w-4 h-4 mr-2" />
            From URL
          </button>
          <button
            role="menuitem"
            onClick={() => { onAddJobManual(); setShowMenu(false); }}
            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
          >
            <FileText className="w-4 h-4 mr-2" />
            Manual Entry
          </button>
          <button
            role="menuitem"
            onClick={() => { onAddJobEmail(); setShowMenu(false); }}
            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
          >
            <Mail className="w-4 h-4 mr-2" />
            From Email
          </button>
        </div>
      )}
    </div>
  );
}
